import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link , useNavigate} from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { io } from 'socket.io-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ImageWithLoader2 from './components/ImageWithLoader2';
import { formatDistanceToNow, parseISO } from 'date-fns';

const isLocalhost = window.location.hostname === 'localhost';
const SOCKET_URL = isLocalhost ? "http://localhost:8081" : "https://oret.africa";

const socket = io(SOCKET_URL, {
  path: "/tree/mobile/backend/socket.io",
  transports: ["polling", "websocket"],
  withCredentials: true,
  autoConnect: false
});

const APP_HEADER_H = 56;         
const BOTTOM_NAV_H = 64;  

function ChatList() {
  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();

  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const queryClient = useQueryClient();

  // Fetch current user ID
  useEffect(() => {
    const id = localStorage.getItem('user_id');
    if (id) setUserId(id);
  }, []);

  // React Query fetch for chat list
  const {
    data: chatLists = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['chat-members-list'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/chat-members-list`);
      return res.data;
    },
    staleTime: 0,
    cacheTime: Infinity,
  });

  // Socket setup
  useEffect(() => {
    if (!userId) return;

    socket.connect();
    socket.emit("join", userId);

    const handleReceiveMessage = (data) => {
      if (data.receiver_id === userId || data.sender_id === userId) {
        queryClient.invalidateQueries(['chat-members-list']); // 🔁 Refresh chat list
      }
    };

    socket.on("receive_message", handleReceiveMessage);

    return () => {
      socket.off("receive_message", handleReceiveMessage);
      socket.disconnect();
    };
  }, [userId, queryClient]);

  //search
  const [searchTerm, setSearchTerm] = useState('');

  const filteredChats = chatLists.filter((chat) => {
    const fullName = `${chat.n_prefix || ''} ${chat.name || ''} ${chat.last_name || ''}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });
  //end search


  return (
    <div
    className="theme-light"
    style={{
      height: '100dvh',
      background: styles.wrapper.background,
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto 1fr auto',
      overflow: 'hidden'
    }}
  >

{isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />

        <main
        style={{
          height: `calc(100dvh - ${BOTTOM_NAV_H}px)`,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
       
          paddingTop: APP_HEADER_H,
          paddingBottom: `calc(env(safe-area-inset-bottom, 0px))`,
        }}
      >
        


        <div style={styles.pageFrame}>
               
                 <div style={styles.searchSection}>
                   <div style={styles.searchContainer}>
                     <div style={styles.searchInputWrapper}>
                       <i className="fas fa-search" style={styles.searchIcon}></i>
                       <input
                         type="text"
                         placeholder="Search messages, people..."
                         value={searchTerm}
                         onChange={(e) => setSearchTerm(e.target.value)}
                         style={styles.searchInput}
                         className="search-input"
                       />
                       {searchTerm && (
                         <button
                           onClick={() => setSearchTerm('')}
                           style={styles.clearButton}
                           className="clear-btn"
                           aria-label="Clear search"
                         >
                           <i className="fas fa-times" style={styles.clearIcon}></i>
                         </button>
                       )}
                     </div>
                   </div>
                 </div>
       
                 
                 <div style={styles.chatListContainer}>
                   
                   {isLoading ? (
                     <div style={styles.loadingContainer}>
                       <div style={styles.loadingSpinner}></div>
                       <h3 style={styles.loadingTitle}>Loading conversations</h3>
                       <p style={styles.loadingText}>Fetching your latest messages...</p>
                     </div>
                   ) : isError ? (
                     <div style={styles.errorContainer}>
                       <div style={styles.errorIconWrapper}>
                         <i className="fas fa-exclamation-triangle" style={styles.errorIcon}></i>
                       </div>
                       <h3 style={styles.errorTitle}>Connection failed</h3>
                       <p style={styles.errorText}>Unable to load your conversations</p>
                       <button
                         style={styles.retryButton}
                         className="retry-btn"
                         onClick={() => queryClient.invalidateQueries(['chat-members-list'])}
                       >
                         <i className="fas fa-redo" style={styles.retryIcon}></i>
                         Try Again
                       </button>
                     </div>
                   ) : filteredChats.length === 0 ? (
                     <div style={styles.emptyContainer}>
                       <div style={styles.emptyIconWrapper}>
                         <i className="fas fa-comments" style={styles.emptyIcon}></i>
                       </div>
                       <h3 style={styles.emptyTitle}>
                         {searchTerm ? 'No matches found' : 'No conversations yet'}
                       </h3>
                       <p style={styles.emptyText}>
                         {searchTerm
                           ? 'Try adjusting your search terms or check the spelling'
                           : 'Start your first conversation and connect with others!'}
                       </p>
                       {!searchTerm && (
                         <button style={styles.startChatButton} className="start-chat-btn">
                           <i className="fas fa-plus" style={styles.startChatIcon}></i>
                           Start New Chat
                         </button>
                       )}
                     </div>
                   ) : (
                     <div style={styles.chatList}>
                       {searchTerm && (
                         <div style={styles.resultsHeader}>
                           <span style={styles.resultsText}>
                             {filteredChats.length} result{filteredChats.length !== 1 ? 's' : ''} for "{searchTerm}"
                           </span>
                         </div>
                       )}
       
                       {filteredChats.map((chat, index) => (
                         <Link

                         to="/chat"
                         state={{ receiverId: chat.user_id }}

                           style={{textDecoration: 'none', 
                            color: 'inherit', 
                            display: 'block',
                             background: chat.family_name ? "#ffefd5" : "#ffffff", }}
                           className="chat-link"

                         >
                           <div
                             style={{ ...styles.chatItem, animationDelay: `${index * 0.05}s` }}
                             className="chat-item"
                           >
                             <div style={styles.avatarContainer}>
                               <div style={styles.avatarWrapper}>
                                
                                 <ImageWithLoader2
                                   src={chat.image_url}
                                   placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                                   fadeDuration={800}
                                   blurAmount={15} 
                                   alt={`${chat.name} ${chat.last_name}`}
                                   style={styles.avatar}
                                   onError={(e) => {
                                     e.currentTarget.onerror = null;
                                     e.currentTarget.src = '/images/default-avatar.png';
                                   }}
                                 />
                                 
                                 <div style={styles.onlineIndicator}></div>
                               </div>
                             </div>
       
                             <div style={styles.chatContent}>
                               <div style={styles.chatHeader}>
                                 <div style={styles.nameSection}>
                                   <span style={styles.chatName}>
                                     {chat.n_prefix} {chat.name} {chat.last_name} 

                                     {chat.family_name && (
                                      <span style={styles.familyName}>
                                        {" "}
                                        ({chat.family_name})
                                      </span>
                                    )}

                                   </span>
                                   {chat.unread_count > 0 && (
                                     <div style={styles.unreadIndicator}></div>
                                   )}
                                 </div>
                                 <span style={styles.chatTime}>{formatDate(chat.created_at)}</span>
                               </div>
       
                               <div style={styles.chatPreviewRow}>
                                 <span
                                   style={{
                                     ...styles.chatPreview,
                                     fontWeight: chat.unread_count > 0 ? 600 : 400,
                                     color: chat.unread_count > 0 ? '#374151' : '#6b7280'
                                   }}
                                 >
                                   {chat.last_message?.slice(0, 50) ?? 'No messages yet...'}
                                 </span>
                                 {chat.unread_count > 0 && (
                                   <div style={styles.unreadBadge} className="unread-badge">
                                     {chat.unread_count > 99 ? '99+' : chat.unread_count}
                                   </div>
                                 )}
                               </div>
                             </div>
       
                             <div style={styles.chatActions}>
                               <i className="fas fa-chevron-right" style={styles.chevronIcon}></i>
                             </div>
                           </div>
                         </Link>
                       ))}
                     </div>
                   )}
                 </div>
       
                
                 <div style={{ height: 16 }} />
               </div> 

      </main>
    </div>
  );
}


function formatDate(date) {
  if (!date) return '';
  const now = new Date();
  const msgDate = new Date(date);

  const today = new Date();
  const isToday = today.toDateString() === msgDate.toDateString();

  const yest = new Date();
  yest.setDate(yest.getDate() - 1);
  const isYesterday = yest.toDateString() === msgDate.toDateString();

  if (isToday) {
    return msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (isYesterday) return 'Yesterday';

  return msgDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: msgDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

const styles = {
  wrapper: {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f3f4f6 100%)',
  },

  
  pageFrame: {
    width: '100%',
    paddingBottom: 0,
  },


  searchSection: {
    padding: '16px',        
    width: '100%',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    marginTop: 8,
  },
  searchContainer: { position: 'relative' },
  searchInputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: { 
    position: 'absolute', 
    left: 16, 
    color: '#9ca3af', 
    fontSize: 16, 
    zIndex: 2,
    transition: 'color 0.2s ease',
  },
  searchInput: {
    width: '100%',
    height: 52,
    borderRadius: 16,
    border: '2px solid rgba(0, 0, 0, 0.08)',
    paddingLeft: 48,
    paddingRight: 48,
    fontSize: 16,
    background: 'rgba(245, 250, 247, 0.8)',
    backdropFilter: 'blur(8px)',
    outline: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontFamily: 'inherit',
    fontWeight: 500,
  },
  clearButton: {
    position: 'absolute',
    right: 12,
    background: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  clearIcon: { color: '#ef4444', fontSize: 12, fontWeight: 600 },

  
  chatListContainer: { 
    width: '100%',
    padding: 0,
  },
  chatList: { 
    padding: 0, 
  },
  resultsHeader: {
    padding: '8px 12px 16px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    marginBottom: 12,
  },
  resultsText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: 600,
  },

  chatLink: { 
    textDecoration: 'none', 
    color: 'inherit', 
    display: 'block',
  },
  chatItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 16px',
    marginBottom: 12,
    borderRadius: 18,
   
    backdropFilter: 'blur(8px)',
    border: '2px solid rgba(0, 0, 0, 0.04)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
  },
  
  avatarContainer: { marginRight: 16, position: 'relative' },
  avatarWrapper: { 
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    width: 54,
    height: 54,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid rgba(46, 143, 102, 0.1)',
    transition: 'all 0.2s ease',
  },
  onlineIndicatorx: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: 'linear-gradient(45deg, #10b981, #059669)',
    border: '3px solid #ffffff',
    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
  },
  
  chatContent: { flex: 1, minWidth: 0, marginRight: 12 },
  chatHeader: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'flex-start', 
    marginBottom: 6,
  },
  nameSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  chatName: { color: '#111827', fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' },
  unreadIndicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'linear-gradient(45deg, #ef4444, #dc2626)',
    flexShrink: 0,
  },

  familyName: {
    fontSize: "12px",     // smaller
    fontStyle: "italic", // italic
    fontWeight: 400,
    color: "#6b7280",     // soft gray
    marginLeft: "4px",
  },

  chatTime: { fontSize: 11, color: '#111827', fontWeight: 400, marginLeft: 8, whiteSpace: 'nowrap' },
  
  chatPreviewRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  chatPreview: {
    fontSize: 14,
    color: '#6b7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    lineHeight: 1.4,
    transition: 'all 0.2s ease',
  },
  
  chatActions: { display: 'flex', alignItems: 'center', paddingLeft: 8 },
  chevronIcon: { fontSize: 12, color: '#d1d5db', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' },
  
  unreadBadge: {
    background: 'linear-gradient(45deg, #ef4444, #dc2626)',
    color: '#ffffff',
    borderRadius: 14,
    fontSize: 12,
    fontWeight: 800,
    padding: '4px 9px',
    minWidth: 24,
    textAlign: 'center',
    boxShadow: '0 3px 10px rgba(239, 68, 68, 0.4)',
    transition: 'all 0.2s ease',
  },

  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' },
  loadingSpinner: { width: 48, height: 48, border: '4px solid rgba(46, 143, 102, 0.1)', borderTop: '4px solid #2e8f66', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 24 },
  loadingTitle: { color: '#111827', fontSize: 18, fontWeight: 700, margin: '0 0 8px 0' },
  loadingText: { color: '#6b7280', fontSize: 15, margin: 0, opacity: 0.8 },

  
  errorContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' },
  errorIconWrapper: { width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  errorIcon: { fontSize: 32, color: '#ef4444' },
  errorTitle: { color: '#111827', fontSize: 20, fontWeight: 700, margin: '0 0 8px 0' },
  errorText: { color: '#6b7280', fontSize: 15, margin: '0 0 24px 0', opacity: 0.8 },
  retryButton: { background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', borderRadius: 12, padding: '12px 24px', color: '#ffffff', fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', alignItems: 'center', gap: 8 },
  retryIcon: { fontSize: 14 },


  emptyContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' },
  emptyIconWrapper: { width: 96, height: 96, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(46, 143, 102, 0.08), rgba(4, 120, 87, 0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 },
  emptyIcon: { fontSize: 40, color: 'rgba(46, 143, 102, 0.6)' },
  emptyTitle: { color: '#111827', fontSize: 22, fontWeight: 700, margin: '0 0 8px 0' },
  emptyText: { color: '#6b7280', fontSize: 15, margin: '0 0 24px 0', maxWidth: 320, lineHeight: 1.5, opacity: 0.9 },
  startChatButton: { background: 'linear-gradient(135deg, #2e8f66, #047857)', border: 'none', borderRadius: 12, padding: '12px 24px', color: '#ffffff', fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', alignItems: 'center', gap: 8 },
  startChatIcon: { fontSize: 14 },
};

export default ChatList;
